<template>
  <div class="flex justify-between my-2 w-80">
    <div>
      <NListbox v-model="day" :placeholder="t('misc.day')" input-size="small">
        <NListboxOption v-for="item in 31" :key="item" :value="item">
          {{ item }}
        </NListboxOption>
      </NListbox>
    </div>

    <div class="px-4">
      <NListbox
        v-model="month"
        :placeholder="t('misc.month')"
        input-size="small"
      >
        <NListboxOption
          v-for="(item, index) in MONTHS"
          :key="item"
          :label="item"
          :value="index"
        >
          {{ item }}
        </NListboxOption>
      </NListbox>
    </div>

    <div>
      <NListbox v-model="year" :placeholder="t('misc.year')" input-size="small">
        <NListboxOption v-for="item in YEARS" :key="item" :value="item">
          {{ item }}
        </NListboxOption>
      </NListbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { MONTHS, YEARS } from './constants';
import { toJalali, toGregorian, convertUTCDateToLocalDate } from './convertor';

type DatePickerProps = {
  modelValue: Date;
  isLocalDateTime?: boolean;
};
type DatepickerEmits = {
  (e: 'update:modelValue', value: Date): void;
};

const props = withDefaults(defineProps<DatePickerProps>(), {
  isLocalDateTime: false,
});
const emit = defineEmits<DatepickerEmits>();

const model = computed(() => {
  const [year, month, day] = toJalali(
    props.modelValue.getFullYear(),
    props.modelValue.getMonth(),
    props.modelValue.getDate()
  );

  return { year, month, day };
});

const year = computed({
  get: () => ({ value: model.value.year }),
  set: ({ value }) => {
    setModel(value, 'year');
  },
});

const month = computed({
  get: () => ({ value: model.value.month, label: MONTHS[model.value.month] }),
  set: ({ value }) => {
    setModel(value, 'month');
  },
});

const day = computed({
  get: () => ({ value: model.value.day }),
  set: ({ value }) => {
    setModel(value, 'day');
  },
});

function setModel(value: number, type: 'day' | 'month' | 'year') {
  const date: {
    year: string | number;
    month: string | number;
    day: string | number;
  } = {
    year: year.value.value,
    month: month.value.value,
    day: day.value.value,
    [type]: value,
  } as const;

  const modelValue = props.isLocalDateTime
    ? convertUTCDateToLocalDate(new Date(...toGregorian(date)))
    : new Date(...toGregorian(date));

  emit('update:modelValue', modelValue);
}

const { t } = useI18n();
</script>
